/**
 * Ficha de centro.
 *
 * La pestaña que justifica esta pantalla es **Cumplimiento**: el motor no
 * sólo dice si el centro está sujeto a inspección periódica, dice **por
 * qué**, con su artículo. Un «sí» sin explicación no sirve para discutir
 * con un cliente que dice que a él eso no le aplica.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';
import { DatumGroup, Datum, DetailShell, type Tab } from './detail-shell.js';
import { MiniList } from './mini-list.js';

interface SiteDetail {
  id: string;
  code: string;
  name: string;
  customer_id: string;
  customer_name: string;
  main_use: string;
  built_area_m2: string | null;
  has_high_risk_area: boolean;
  has_storage_area: boolean;
  is_industrial_establishment: boolean;
  fire_load_qs_mj_m2: string | null;
  construction_date: string | null;
  only_exempt_equipment: boolean;
  inspection_required: boolean | null;
  inspection_interval_years: number | null;
  last_inspection_date: string | null;
  access_instructions: string | null;
  keys_info: string | null;
  risks: string | null;
  notes: string | null;
}

interface Compliance {
  required: boolean;
  intervalYears: number | null;
  reason: string;
  articleRef: string;
  matrixRefs?: string[];
}

interface EquipmentRow {
  id: string;
  internal_code: string;
  equipment_type: string;
  status: string;
  next_due_date: string | null;
  days_to_due: number | null;
}

interface InspectionRow {
  id: string;
  number: string;
  performed_on: string;
  inspection_type: string;
  status: string;
  technician_name: string | null;
  anomaly_count: string;
}

const USE: Record<string, string> = {
  RESIDENCIAL_VIVIENDA: 'Residencial vivienda',
  ADMINISTRATIVO: 'Administrativo',
  DOCENTE: 'Docente',
  COMERCIAL: 'Comercial',
  PUBLICA_CONCURRENCIA: 'Pública concurrencia',
  APARCAMIENTO: 'Aparcamiento',
  INDUSTRIAL: 'Industrial',
  HOSPITALARIO: 'Hospitalario',
  OTROS: 'Otros',
};

export function SiteDetailScreen(): React.ReactNode {
  const { api } = useSession();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<SiteDetail | null>(null);
  const [compliance, setCompliance] = useState<Compliance | null>(null);
  const [complianceError, setComplianceError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [starting, setStarting] = useState(false);

  /**
   * Abre una revisión de mantenimiento sobre todos los equipos del centro.
   *
   * Sin `equipmentIds`, el servidor planifica los que hay: es lo que se
   * quiere en una visita normal. Elegir un subconjunto es el caso raro y
   * se hace desde la propia revisión.
   */
  async function startInspection(): Promise<void> {
    setStarting(true);
    try {
      const created = await api.post<{ id: string }>('/inspections', {
        siteId: id,
        performedOn: new Date().toISOString().slice(0, 10),
        inspectionType: 'MANTENIMIENTO',
      });
      void navigate(`/revisiones/${created.id}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido abrir la revisión.');
    } finally {
      setStarting(false);
    }
  }

  const countSetter = useCallback(
    (key: string) => (total: number | null) => setCounts((c) => ({ ...c, [key]: total })),
    [],
  );

  useEffect(() => {
    let cancelled = false;

    void Promise.all([
      api.get<SiteDetail>(`/sites/${id}`).then((data) => {
        if (!cancelled) setDetail(data);
      }),
      api
        .get<Compliance>(`/sites/${id}/compliance`)
        .then((data) => {
          if (!cancelled) setCompliance(data);
        })
        .catch((caught: unknown) => {
          // El cumplimiento puede fallar por sí solo —falta un dato del
          // centro— sin que eso impida ver el resto de la ficha.
          if (!cancelled) {
            setComplianceError(
              caught instanceof ApiError ? caught.message : 'No se ha podido calcular el cumplimiento.',
            );
          }
        }),
    ])
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar el centro.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  const tabs: Tab[] = [
    {
      id: 'resumen',
      label: 'Resumen',
      render: () => (
        <>
          <DatumGroup title="Identificación">
            <Datum label="Código">{detail?.code}</Datum>
            <Datum label="Centro">{detail?.name}</Datum>
            <Datum label="Cliente">
              {detail && <Link to={`/clientes/${detail.customer_id}`}>{detail.customer_name}</Link>}
            </Datum>
            <Datum label="Uso principal">{USE[detail?.main_use ?? ''] ?? detail?.main_use}</Datum>
            <Datum label="Superficie construida">
              {detail?.built_area_m2 ? `${detail.built_area_m2} m²` : null}
            </Datum>
          </DatumGroup>

          <DatumGroup title="Acceso">
            <Datum label="Instrucciones">{detail?.access_instructions}</Datum>
            <Datum label="Llaves">{detail?.keys_info}</Datum>
            <Datum label="Riesgos">{detail?.risks}</Datum>
            <Datum label="Notas">{detail?.notes}</Datum>
          </DatumGroup>
        </>
      ),
    },
    {
      id: 'cumplimiento',
      label: 'Cumplimiento',
      render: () => (
        <>
          {complianceError !== null && (
            <p className="screen__error" role="alert">
              {complianceError}
            </p>
          )}

          {compliance && (
            <p className={`screen__note${compliance.required ? ' screen__note--strong' : ''}`}>
              {compliance.required ? (
                <>
                  Este centro <strong>está sujeto a inspección periódica</strong> cada{' '}
                  {compliance.intervalYears ?? '—'} años por un organismo de control.
                </>
              ) : (
                <>Este centro no está sujeto a inspección periódica.</>
              )}{' '}
              {/* El porqué, no sólo el sí o el no: es lo que permite
                  discutirlo con un cliente que cree que a él no le aplica. */}
              {compliance.reason} <em>({compliance.articleRef})</em>
              {compliance.matrixRefs && compliance.matrixRefs.length > 0 && (
                <> · Matriz: {compliance.matrixRefs.join(', ')}</>
              )}
            </p>
          )}

          <DatumGroup title="Datos que deciden la obligación">
            <Datum
              label="Zonas de riesgo especial alto"
              help="Anulan cualquier exención por superficie o uso."
            >
              {detail?.has_high_risk_area ? 'Sí' : 'No'}
            </Datum>
            <Datum label="Zonas de almacén" help="Anulan cualquier exención.">
              {detail?.has_storage_area ? 'Sí' : 'No'}
            </Datum>
            <Datum
              label="Establecimiento industrial"
              help="Pasa al RD 164/2025, con otro régimen de obligaciones."
            >
              {detail?.is_industrial_establishment ? 'Sí' : 'No'}
            </Datum>
            <Datum label="Carga de fuego Qs">
              {detail?.fire_load_qs_mj_m2 ? `${detail.fire_load_qs_mj_m2} MJ/m²` : null}
            </Datum>
            <Datum
              label="Fecha de construcción"
              help="Determina el régimen industrial aplicable (DT 1.ª RD 164/2025)."
            >
              {detail?.construction_date}
            </Datum>
            <Datum label="Sólo equipos exentos" help="Supuesto del art. 20.2.">
              {detail?.only_exempt_equipment ? 'Sí' : 'No'}
            </Datum>
            <Datum label="Última inspección periódica">{detail?.last_inspection_date}</Datum>
          </DatumGroup>
        </>
      ),
    },
    {
      id: 'equipos',
      label: 'Equipos',
      count: counts['equipos'] ?? null,
      render: () => (
        <MiniList<EquipmentRow>
          path="/equipment"
          params={{ siteId: id ?? '' }}
          seeAll={`/equipos?q=${encodeURIComponent(detail?.name ?? '')}`}
          emptyMessage="Este centro no tiene equipos dados de alta."
          onCount={countSetter('equipos')}
          limit={15}
          columns={[
            {
              label: 'Código',
              render: (r) => <Link to={`/equipos/${r.id}`}>{r.internal_code}</Link>,
            },
            { label: 'Tipo', render: (r) => r.equipment_type },
            { label: 'Estado', render: (r) => r.status },
            {
              label: 'Próximo vencimiento',
              render: (r) =>
                r.next_due_date === null ? (
                  <span className="due--none">Sin fecha</span>
                ) : (r.days_to_due ?? 0) < 0 ? (
                  <span className="due due--late">
                    ⚠ Vencido hace {Math.abs(r.days_to_due ?? 0)} d · {r.next_due_date}
                  </span>
                ) : (
                  r.next_due_date
                ),
            },
          ]}
        />
      ),
    },
    {
      id: 'revisiones',
      label: 'Revisiones',
      count: counts['revisiones'] ?? null,
      render: () => (
        <MiniList<InspectionRow>
          path="/inspections"
          params={{ siteId: id ?? '' }}
          seeAll="/revisiones?v=all"
          emptyMessage="Este centro no tiene revisiones registradas."
          onCount={countSetter('revisiones')}
          columns={[
            { label: 'Nº', render: (r) => r.number },
            { label: 'Fecha', render: (r) => r.performed_on },
            { label: 'Tipo', render: (r) => r.inspection_type },
            { label: 'Estado', render: (r) => r.status },
            { label: 'Técnico', render: (r) => r.technician_name ?? '—' },
            {
              label: 'Anomalías',
              numeric: true,
              render: (r) =>
                Number(r.anomaly_count) > 0 ? (
                  <span className="due due--soon">{r.anomaly_count}</span>
                ) : (
                  '0'
                ),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DetailShell
      title={detail?.name ?? 'Centro'}
      subtitle={detail ? `${detail.code} · ${detail.customer_name}` : undefined}
      badges={
        detail &&
        (detail.inspection_required === null ? (
          <span className="pill pill--warn">? Inspección sin determinar</span>
        ) : detail.inspection_required ? (
          <span className="pill pill--info">
            Inspección cada {detail.inspection_interval_years ?? '—'} años
          </span>
        ) : (
          <span className="pill pill--off">No sujeto a inspección</span>
        ))
      }
      actions={
        <>
          <Link className="button button--quiet button--small" to={`/centros/${id}/editar`}>
            Editar
          </Link>
          <Link className="button button--quiet button--small" to={`/equipos/nuevo?siteId=${id}`}>
            Nuevo equipo
          </Link>
          <Link className="button button--quiet button--small" to={`/ordenes/nueva?siteId=${id}`}>
            Nueva orden
          </Link>
          <button
            className="button button--primary button--small"
            type="button"
            disabled={starting}
            onClick={() => void startInspection()}
          >
            {starting ? 'Abriendo…' : 'Iniciar revisión'}
          </button>
        </>
      }
      tabs={tabs}
      loading={loading}
      error={error}
    />
  );
}
